import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { keyframes } from 'styled-components';

// Subtle star twinkle animation
const twinkle = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 0.8; }
  100% { opacity: 0.3; }
`;

// Subtle floating animation
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

const FooterContainer = styled.footer`
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(10px);
  color: rgba(255, 255, 255, 0.9);
  padding: 3.5rem 0 1.5rem;
  margin-top: auto;
  position: relative;
  overflow: hidden;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.2);
  
  /* Space background effect */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 10% 20%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 90% 60%, rgba(124, 58, 237, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 50% 100%, rgba(79, 70, 229, 0.1) 0%, transparent 40%);
    z-index: -2;
  }
  
  /* Stars effect */
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: 
      radial-gradient(1px 1px at 5% 10%, rgba(255, 255, 255, 0.8) 1px, transparent 0),
      radial-gradient(1px 1px at 15% 25%, rgba(255, 255, 255, 0.7) 1px, transparent 0),
      radial-gradient(1px 1px at 25% 40%, rgba(255, 255, 255, 0.9) 1px, transparent 0),
      radial-gradient(1px 1px at 35% 15%, rgba(255, 255, 255, 0.7) 1px, transparent 0),
      radial-gradient(1px 1px at 45% 35%, rgba(255, 255, 255, 0.8) 1px, transparent 0),
      radial-gradient(1px 1px at 55% 10%, rgba(255, 255, 255, 0.7) 1px, transparent 0),
      radial-gradient(1px 1px at 65% 30%, rgba(255, 255, 255, 0.9) 1px, transparent 0),
      radial-gradient(1px 1px at 75% 15%, rgba(255, 255, 255, 0.7) 1px, transparent 0),
      radial-gradient(1px 1px at 85% 45%, rgba(255, 255, 255, 0.8) 1px, transparent 0),
      radial-gradient(1px 1px at 95% 25%, rgba(255, 255, 255, 0.7) 1px, transparent 0);
    z-index: -1;
    opacity: 0.6;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
  position: relative;
  z-index: 1;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2.5rem;
`;

const FooterColumn = styled.div`
  h3 {
    color: rgba(255, 255, 255, 0.95);
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
    font-weight: 600;
    position: relative;
    letter-spacing: 0.5px;
    text-shadow: 0 0 10px rgba(99, 102, 241, 0.5);
    
    &:after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 0;
      width: 60px;
      height: 2px;
      background: linear-gradient(to right, 
        rgba(99, 102, 241, 0.8), 
        rgba(129, 140, 248, 0.4), 
        transparent
      );
      border-radius: 1px;
    }
  }
  
  p {
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.7;
    margin-bottom: 1.2rem;
    font-size: 0.95rem;
  }
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  animation: ${float} 6s ease-in-out infinite;
`;

const LogoImage = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(79, 70, 229, 0.9) 100%);
  border-radius: 10px;
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.1rem;
  position: relative;
  overflow: hidden;
  box-shadow: 
    0 8px 16px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(255, 255, 255, 0.15),
    inset 0 0 10px rgba(255, 255, 255, 0.2);
    
  /* Glassy shine effect */
  &:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 45%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0.1) 55%,
      transparent 100%
    );
    transform: rotate(45deg);
    animation: ${twinkle} 4s infinite;
  }
`;

const LogoText = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: 0.5px;
  text-shadow: 0 0 15px rgba(99, 102, 241, 0.4);
  
  span {
    color: rgba(99, 102, 241, 0.95);
    position: relative;
    
    &:before {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 2px;
      background: linear-gradient(
        to right,
        transparent,
        rgba(99, 102, 241, 0.8),
        transparent
      );
      transition: width 0.3s ease;
    }
    
    &:hover {
      text-shadow: 0 0 20px rgba(99, 102, 241, 0.6);
      
      &:before {
        width: 100%;
      }
    }
  }
`;

const FooterLink = styled(Link)`
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  position: relative;
  display: block;
  padding: 0.4rem 0;
  margin-bottom: 0.5rem;

  &:before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 1px;
    background: linear-gradient(
      to right,
      rgba(99, 102, 241, 0.8),
      rgba(124, 58, 237, 0.8)
    );
    transition: width 0.3s ease;
  }

  &:after {
    content: '›';
    position: absolute;
    left: -15px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;
    transition: all 0.3s ease;
    color: rgba(99, 102, 241, 0.95);
  }

  &:hover {
    color: rgba(255, 255, 255, 0.95);
    text-shadow: 0 0 10px rgba(99, 102, 241, 0.4);
    padding-left: 15px;

    &:before {
      opacity: 1;
      left: 0;
    }
  }
`;

const FooterLinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLinkItem = styled.li`
  margin-bottom: 0.6rem;
  position: relative;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.2rem;
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(5px);
  color: rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  
  /* Glassy shine effect */
  &:before {
    content: '';
    position: absolute;
    top: -100%;
    left: -100%;
    width: 300%;
    height: 300%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 45%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0.1) 55%,
      transparent 100%
    );
    transform: rotate(45deg);
    transition: all 0.6s ease;
  }
  
  &:hover {
    background: rgba(99, 102, 241, 0.7);
    transform: translateY(-5px);
    box-shadow: 
      0 10px 20px rgba(0, 0, 0, 0.2),
      0 0 0 1px rgba(99, 102, 241, 0.3),
      0 0 15px rgba(99, 102, 241, 0.5);
      
    &:before {
      top: 100%;
      left: 100%;
    }
    
    i {
      text-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
    }
  }
  
  i {
    font-size: 1.1rem;
  }
`;

const Divider = styled.div`
  margin-top: 3rem;
  padding-top: 1.5rem;
  text-align: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 15%;
    right: 15%;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      rgba(99, 102, 241, 0.3),
      rgba(255, 255, 255, 0.2),
      rgba(99, 102, 241, 0.3),
      transparent
    );
  }
  
  p {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
    letter-spacing: 0.5px;
  }
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          <FooterColumn>
            <FooterLogo>
              <LogoImage>IC</LogoImage>
              <LogoText>
                Inter<span>Connect</span>
              </LogoText>
            </FooterLogo>
            <p>Connecting students with valuable internship opportunities to build their careers and gain real-world experience.</p>
            <p>A platform for universities, companies, and students to collaborate effectively.</p>
          </FooterColumn>
          
          <FooterColumn>
            <h3>Quick Links</h3>
            <FooterLinkList>
              <FooterLinkItem>
                <FooterLink to="/">Home</FooterLink>
              </FooterLinkItem>
              <FooterLinkItem>
                <FooterLink to="/internships">Internships</FooterLink>
              </FooterLinkItem>
              <FooterLinkItem>
                <FooterLink to="/company-register">Company Registration</FooterLink>
              </FooterLinkItem>
              <FooterLinkItem>
                <FooterLink to="/dashboard">Dashboard</FooterLink>
              </FooterLinkItem>
            </FooterLinkList>
          </FooterColumn>
          
          <FooterColumn>
            <h3>Resources</h3>
            <FooterLinkList>
              <FooterLinkItem>
                <FooterLink to="/faq">FAQ</FooterLink>
              </FooterLinkItem>
              <FooterLinkItem>
                <FooterLink to="/guidelines">Internship Guidelines</FooterLink>
              </FooterLinkItem>
              <FooterLinkItem>
                <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
              </FooterLinkItem>
              <FooterLinkItem>
                <FooterLink to="/terms">Terms of Service</FooterLink>
              </FooterLinkItem>
            </FooterLinkList>
          </FooterColumn>
          
          <FooterColumn>
            <h3>Contact</h3>
            <p>Email: scad@example.edu</p>
            <p>Phone: +20 (2) 2615-2694</p>
            <p>Address: New Cairo, Cairo Governorate 11835</p>
            <SocialLinks>
              <SocialIcon href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <i className="fab fa-facebook-f"></i>
              </SocialIcon>
              <SocialIcon href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <i className="fab fa-twitter"></i>
              </SocialIcon>
              <SocialIcon href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <i className="fab fa-linkedin-in"></i>
              </SocialIcon>
              <SocialIcon href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <i className="fab fa-instagram"></i>
              </SocialIcon>
            </SocialLinks>
          </FooterColumn>
        </FooterGrid>
        
        <Divider>
          <p>&copy; {new Date().getFullYear()} InterConnect. All rights reserved.</p>
        </Divider>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;